import { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { getMediaTitleForWidth } from './mediaUtils';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type ProcessingPlaceholderProps = {
  processing: boolean;
  width: number;
  height: number;
  displayMode: 'outlined' | 'card';
  url: string;
  name: string;
  extension: string;
  children: React.ReactNode;
};

// Server-side transcoding of a full-resolution phone photo runs into the tens of
// seconds, so the budget has to outlast it by a wide margin; backing off keeps
// the request count low across that window instead of hammering a 404.
const MAX_WAIT_MS = 120000;
const INITIAL_DELAY_MS = 1000;
const MAX_DELAY_MS = 8000;

export const ProcessingPlaceholder = (props: ProcessingPlaceholderProps) => {
  const [checkingResource, setCheckingResource] = useState(props.processing);

  //todo: turn into a hook for further reuse
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    let delay = INITIAL_DELAY_MS;
    const giveUpAt = Date.now() + MAX_WAIT_MS;

    setCheckingResource(props.processing);

    const scheduleRetry = () => {
      // Giving up has to reveal the child rather than leave the indicator
      // running: a spinner that never resolves reads as a hung upload, and no
      // later render can clear it because nothing re-runs this effect.
      if (Date.now() >= giveUpAt) {
        setCheckingResource(false);
        return;
      }
      timeoutId = setTimeout(checkResource, delay);
      delay = Math.min(delay * 2, MAX_DELAY_MS);
    };

    const checkResource = async () => {
      try {
        const response = await fetch(props.url, { method: 'HEAD' });
        if (cancelled) return;
        if (response.ok) {
          setCheckingResource(false);
          return;
        }
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to check resource:', e);
      }
      scheduleRetry();
    };

    if (props.processing && props.url) {
      void checkResource();
    }

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [props.processing, props.url]);

  const containerStyle =
    props.displayMode === 'outlined' ? styles.outlined : styles.card;

  return checkingResource ? (
    <View
      style={[containerStyle, { width: props.width, height: props.height }]}
    >
      <ActivityIndicator color={Colors.primary} size={24} />
      <Text style={styles.mediaTitle}>
        {getMediaTitleForWidth(props.name, props.extension, props.width)}
      </Text>
    </View>
  ) : (
    <>{props.children}</>
  );
};

const styles = themedStyleSheet((role) => ({
  outlined: {
    borderWidth: 0.5,
    borderColor: role.borderStrong,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: role.surfaceRaised,
  },
  card: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
    elevation: 3,
    boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 3px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: role.surfaceRaised,
  },
  mediaTitle: {
    marginTop: 8,
    textAlign: 'center',
    fontWeight: '500',
    fontSize: 12,
    width: '100%',
    color: role.textStrong,
  },
}));
